// API Service - Secure Backend Calls
// All API calls go through your Vercel serverless functions
// Includes DEMO MODE for previews without backend

import { getAuthToken, supabase, isDemoMode } from '../lib/supabase';
import type { ScriptFormData } from '../types';

// For local dev, use relative path. Vercel handles this automatically.
const API_BASE = '/api';

export interface UsageInfo {
  used: number;
  limit: number;
  remaining: number;
}

export interface GenerateResponse {
  script: string;
  usage: UsageInfo;
}

export interface SavedTemplate {
  id: string;
  title: string | null;
  form_data: ScriptFormData;
  script: string;
  created_at: string;
}

// Demo script for preview mode
const DEMO_SCRIPT = `**Catchy Title:** The 2024 Honda Accord That's Breaking The Internet 🔥

**Hook (First 3 Seconds):** "POV: You just found out this Accord gets 51 MPG and looks THIS good..."

**Scene 1:**
*Visual:* Slow cinematic pan around the exterior, sunset lighting
*Dialogue:* "Alright fam, let me show you why everyone's talking about this car..."
*On-Screen Text:* "2024 HONDA ACCORD HYBRID"

**Scene 2:**
*Visual:* Interior walkthrough - leather seats, digital dash, sunroof opening
*Dialogue:* "Full leather interior, panoramic sunroof, and a screen bigger than your TV at home"
*On-Screen Text:* "THIS IS LUXURY 👀"

**Scene 3:**
*Visual:* Quick cuts of the car driving, speedometer, city shots
*Dialogue:* "51 miles per gallon. Fifty. One. Your wallet is literally thanking you right now."
*On-Screen Text:* "51 MPG HYBRID 🌱"

**Call to Action (CTA):** 
"Link in bio to schedule your test drive - or just comment 'ACCORD' and I'll hook you up! 🔗"

**Suggested Hashtags:**
#HondaAccord #2024Accord #CarTok #HybridCar #FuelEfficient #LuxuryCars #CarSales #DealershipLife #NewCarFeeling #HondaGang`;

// Demo templates for preview mode
const DEMO_TEMPLATES: SavedTemplate[] = [
  {
    id: 'demo-1',
    title: '2024 Honda Accord - Viral Script',
    form_data: {
      videoType: 'Vehicle-Focused' as any,
      make: 'Honda',
      model: 'Accord',
      year: '2024',
      keyFeatures: 'Hybrid engine\nLeather seats\nSunroof',
      targetAudience: 'Young professionals',
      videoStyle: 'Funny & Relatable' as any,
      cta: 'Book a test drive' as any,
    },
    script: DEMO_SCRIPT,
    created_at: new Date().toISOString(),
  },
];

// User-friendly error messages (no technical jargon for demos)
const friendlyErrors: Record<string, string> = {
  '429': 'Our AI is a bit busy right now. Please try again in a moment.',
  '500': 'Something went wrong on our end. Please try again.',
  '503': 'Service temporarily unavailable. Please try again shortly.',
  '403': 'Subscription Required. Please visit the store at scpagency.ai/home to activate your account.',
  'network': 'Connection issue. Please check your internet and try again.',
  'timeout': 'Request took too long. Please try again.',
  'auth': 'Please log in to continue.',
  'default': 'Unable to generate script. Please try again.',
};

// Retry helper with exponential backoff (silent retries)
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);

      // Don't retry client errors (4xx except 429)
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        return response;
      }

      // Retry on 429 (rate limit) or 5xx (server errors)
      if (response.status === 429 || response.status >= 500) {
        if (attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }

      return response;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error('Network error');
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
}

export const ApiService = {
  /**
   * Generate a viral script using Gemini (secure server-side call)
   * Includes automatic retry and user-friendly error handling
   */
  async generateScript(formData: ScriptFormData): Promise<GenerateResponse> {
    if (isDemoMode) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      return {
        script: DEMO_SCRIPT.replace('Honda', formData.make || 'Honda')
          .replace('Accord', formData.model || 'Accord')
          .replace('2024', formData.year || '2024'),
        usage: {
          used: 5,
          limit: 100,
          remaining: 95,
        },
      };
    }

    const token = await getAuthToken();

    if (!token) {
      throw new Error(friendlyErrors.auth);
    }

    try {
      const response = await fetchWithRetry(`${API_BASE}/generate-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const statusStr = response.status.toString();
        const friendlyMsg = friendlyErrors[statusStr] || friendlyErrors.default;
        throw new Error(friendlyMsg);
      }

      return response.json();
    } catch (err) {
      // If it's already a friendly error, rethrow
      if (err instanceof Error && Object.values(friendlyErrors).includes(err.message)) {
        throw err;
      }
      // Otherwise, wrap in friendly message
      throw new Error(friendlyErrors.network);
    }
  },

  /**
   * Get all saved templates for current user
   */
  async getTemplates(): Promise<SavedTemplate[]> {
    if (isDemoMode) {
      return DEMO_TEMPLATES;
    }

    const { data, error } = await supabase
      .from('templates')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return data || [];
  },

  /**
   * Save a template for current user
   */
  async saveTemplate(formData: ScriptFormData, script: string, title?: string): Promise<SavedTemplate> {
    if (isDemoMode) {
      const newTemplate: SavedTemplate = {
        id: `demo-${Date.now()}`,
        form_data: formData,
        script,
        title: title || `${formData.make || 'Trending'} ${formData.model || 'Script'} - ${new Date().toLocaleDateString()}`,
        created_at: new Date().toISOString(),
      };
      DEMO_TEMPLATES.unshift(newTemplate);
      return newTemplate;
    }

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Please log in to save templates');
    }

    const { data, error } = await supabase
      .from('templates')
      .insert({
        user_id: user.id,
        form_data: formData,
        script,
        title: title || `${formData.make || 'Trending'} ${formData.model || 'Script'} - ${new Date().toLocaleDateString()}`,
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Delete a saved template
   */
  async deleteTemplate(id: string): Promise<void> {
    if (isDemoMode) {
      const index = DEMO_TEMPLATES.findIndex(t => t.id === id);
      if (index > -1) DEMO_TEMPLATES.splice(index, 1);
      return;
    }

    const { error } = await supabase
      .from('templates')
      .delete()
      .eq('id', id);

    if (error) throw new Error(error.message);
  },

  /**
   * Get current month's usage stats
   */
  async getUsage(): Promise<UsageInfo | null> {
    if (isDemoMode) {
      return {
        used: 5,
        limit: 100,
        remaining: 95,
      };
    }

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return null;

    const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;

    const { data, error } = await supabase
      .from('usage')
      .select('generations')
      .eq('user_id', user.id)
      .eq('month', currentMonth)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Usage fetch error:', error);
      return null;
    }

    const used = data?.generations || 0;
    // IMPORTANT: This must match MONTHLY_LIMIT in api/generate-script.ts
    const limit = 100;

    return {
      used,
      limit,
      remaining: limit - used,
    };
  },
};
